import { useEffect, useState } from 'react';
import api from '../services/api';
import SEO from '../components/SEO';
import MathText from '../components/MathText';
import { canUserManageContent } from '../utils/rbac';

/**
 * DocumentViewerPage — wraps document PDF viewing inside the website.
 * Fetches a presigned URL from the backend and embeds R2 content directly
 * in an iframe. No file data flows through the backend.
 */
const DocumentViewerPage = ({ documentId, onBack, user }) => {
  const [doc, setDoc] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!documentId) return;

    const load = async () => {
      try {
        let docData;
        let filesData;

        try {
          // Attempt to fetch as Document
          const [docRes, filesRes] = await Promise.all([
            api.get(`/documents/${documentId}`),
            api.get(`/files/presign-all/${documentId}?type=document`),
          ]);
          docData = docRes.data;
          filesData = filesRes.data.files || [];
        } catch (err) {
          if (err.response?.status === 404) {
            // Fallback to Exam
            const [examRes, filesRes] = await Promise.all([
              api.get(`/exams/${documentId}`),
              api.get(`/files/presign-all/${documentId}?type=exam`),
            ]);
            docData = examRes.data;
            filesData = filesRes.data.files || [];
          } else {
            throw err;
          }
        }

        setDoc(docData);
        setFiles(filesData);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load document');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [documentId]);

  if (loading) {
    return (
      <div className="page-card" style={{ textAlign: 'center', padding: '60px' }}>
        <div className="spinner" />
        <p style={{ color: 'var(--text-muted)', marginTop: '16px' }}>Loading document…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-card" style={{ textAlign: 'center', padding: '60px' }}>
        <button className="secondary" onClick={onBack} style={{ marginBottom: '16px' }}>← Back</button>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  if (!doc) return null;

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <SEO
        title={`${doc.title} | Study Centro`}
        description={`View ${doc.title} — ${doc.type || 'document'}. Study materials on Study Centro.`}
      />

      {/* Header bar */}
      <header
        className="test-header"
        style={{
          padding: '12px 32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid var(--border-light)',
          background: 'var(--bg-card)',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', minWidth: 0 }}>
          <button className="secondary submit-btn-small" type="button" onClick={onBack}>
            <i className="fas fa-arrow-left" /> Back
          </button>
          <strong
            style={{
              fontSize: '1rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            <MathText key={doc.title} text={doc.title} />
          </strong>
          {doc.type && (
            <span
              style={{
                fontSize: '0.7rem',
                background: 'rgba(79, 70, 229, 0.1)',
                color: 'var(--primary)',
                padding: '2px 8px',
                borderRadius: '4px',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                flexShrink: 0,
              }}
            >
              {doc.type}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
          {canUserManageContent(user, doc) && files.map((fileObj, idx) => (
            <a
              key={idx}
              href={`/document/${documentId}`}
              target="_blank"
              rel="noreferrer"
              className="premium-icon-btn"
              title={`Open ${files.length > 1 ? fileObj.name : 'in New Tab'}`}
              style={{
                color: 'var(--text-main)',
                textDecoration: 'none',
              }}
            >
              <i className="fas fa-external-link-alt" />
            </a>
          ))}
        </div>
      </header>

      {/* PDF Viewer */}
      <div
        style={{
          flex: 1,
          background: '#525659',
          display: 'flex',
          flexDirection: files.length > 1 ? 'row' : 'column',
          gap: '8px',
          padding: '8px',
        }}
      >
        {files.length > 0 ? (
          files.map((fileObj, idx) => (
            <div key={idx} style={{ flex: 1, height: '100%' }}>
              <iframe
                src={`${fileObj.presignedUrl}#toolbar=0`}
                title={fileObj.name || doc.title}
                style={{ width: '100%', height: '100%', border: 'none', borderRadius: '4px' }}
              />
            </div>
          ))
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              color: 'white',
              opacity: 0.6,
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <i className="fas fa-file-alt fa-3x" style={{ marginBottom: '16px' }} />
              <p>No files available for this document</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentViewerPage;
